import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Boxes, Building2, LayoutTemplate, Pencil, Trash2, User } from "lucide-react";
import { Modal } from "../../../../shared/ui/modal";
import { useScenes } from "../../application/useScenes";
import { launchConstellation } from "../../application/constellationSessions";
import type { ConstellationScene } from "../../domain/types";

type Props = { isOpen: boolean; onClose: () => void };

/** Home-tool "Saved scenes" surface: lists the therapist's scenes and opens a
 *  new board seeded from a chosen one (issue #197). Rename + delete inline. */
export default function ConstellationScenesModal({ isOpen, onClose }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { scenes, status, rename, remove } = useScenes();
    const [opening, setOpening] = useState<string | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");

    const openBoardFromScene = async (scene: ConstellationScene) => {
        setOpening(scene.id);
        try {
            const data = await launchConstellation(
                "anonymous",
                null,
                undefined,
                scene.name,
                scene.id,
            );
            onClose();
            navigate(`/constellation/${data.board.id}`);
        } finally {
            setOpening(null);
        }
    };

    const commitRename = async (sceneId: string) => {
        const name = renameValue.trim();
        if (name) await rename(sceneId, name);
        setRenamingId(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="mx-4 max-w-2xl rounded-3xl">
            <div className="p-6">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                    <Boxes size={20} /> {t("constellation.scenes.saved")}
                </h2>

                {status === "loading" && (
                    <p className="py-8 text-center text-sm text-gray-500">
                        {t("constellation.workspace.loading")}
                    </p>
                )}
                {status === "error" && (
                    <p className="py-8 text-center text-sm text-red-600" role="alert">
                        {t("constellation.scenes.loadError")}
                    </p>
                )}
                {status === "ready" && scenes.length === 0 && (
                    <p className="py-8 text-center text-sm text-gray-500">
                        {t("constellation.scenes.empty")}
                    </p>
                )}

                <ul className="flex flex-col gap-2">
                    {scenes.map((scene) => (
                        <li
                            key={scene.id}
                            className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2 dark:border-gray-800"
                        >
                            <span className="text-content-tertiary">
                                {scene.is_template ? (
                                    <LayoutTemplate size={16} />
                                ) : (
                                    <Boxes size={16} />
                                )}
                            </span>
                            <div className="min-w-0 flex-1">
                                {renamingId === scene.id ? (
                                    <input
                                        autoFocus
                                        value={renameValue}
                                        maxLength={60}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onBlur={() => commitRename(scene.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") commitRename(scene.id);
                                            if (e.key === "Escape") setRenamingId(null);
                                        }}
                                        className="w-full rounded border px-2 py-1 text-sm"
                                    />
                                ) : (
                                    <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                                        {scene.name}
                                    </p>
                                )}
                                <div className="flex items-center gap-2 text-xs text-content-tertiary">
                                    {scene.patient_id && (
                                        <span className="flex items-center gap-1">
                                            <User size={11} />
                                        </span>
                                    )}
                                    {scene.is_template && (
                                        <span>{t("constellation.scenes.template")}</span>
                                    )}
                                    {scene.shared_with_center && (
                                        <span className="flex items-center gap-1 rounded bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                                            <Building2 size={11} aria-hidden="true" />
                                            {t("constellation.scenes.centerBadge")}
                                        </span>
                                    )}
                                    {scene.author_name && (
                                        <span>
                                            {t("constellation.scenes.by", {
                                                name: scene.author_name,
                                            })}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => openBoardFromScene(scene)}
                                disabled={opening === scene.id}
                                className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                            >
                                {t("constellation.scenes.open")}
                            </button>
                            {/* A colleague's centre scene is theirs, or an
                                administrator's, to change. */}
                            {scene.can_edit !== false && (
                                <>
                                    <button
                                        type="button"
                                        aria-label={t("constellation.scenes.rename")}
                                        onClick={() => {
                                            setRenamingId(scene.id);
                                            setRenameValue(scene.name);
                                        }}
                                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={t("constellation.scenes.delete")}
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    t("constellation.scenes.deleteConfirm"),
                                                )
                                            ) {
                                                void remove(scene.id);
                                            }
                                        }}
                                        className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </Modal>
    );
}
